import { io, Socket } from 'socket.io-client';
import logger from './logger';
import config from '../config';

export interface BattleServerToClientEvents {
  'battle.restored': (data: any) => void;
  'battle.round-result': (data: any) => void;
  'battle.action-submitted': () => void;
  'battle.opponent-action-submitted': () => void;
  'battle.finished': (data: { winner: string; experienceGained: number }) => void;
  'battle.error': (data: { error: string }) => void;
  'error': (data: { error: string }) => void;
}

export interface BattleClientToServerEvents {
  'battle.restore': () => void;
  'battle.submit-action': (data: { battleId: string; action: any }) => void;
}

class BattleSocket {
  private socket: Socket<BattleServerToClientEvents, BattleClientToServerEvents> | null = null;
  private listeners: Map<string, Set<Function>> = new Map();

  // Удаляем sessionToken, так как используем cookie
  setSessionToken(token: string) {
    // Оставляем для совместимости, но не используем
  }

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Если уже подключены
      if (this.socket?.connected) {
        resolve();
        return;
      }

      // Если есть старый сокет, отключаем
      if (this.socket) {
        this.disconnect();
      }

      logger.info('BattleSocket', 'Connecting to battle namespace...');

      this.socket = io(`${config.api.wsUrl}/battle`, {
        transports: ['websocket'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
        withCredentials: true // Важно для отправки cookies
      });

      // Регистрируем все обработчики событий
      const events: (keyof BattleServerToClientEvents)[] = [
        'battle.restored',
        'battle.round-result',
        'battle.action-submitted',
        'battle.opponent-action-submitted',
        'battle.finished',
        'battle.error',
        'error'
      ];

      events.forEach(event => {
        this.socket!.on(event as any, (data: any) => {
          logger.debug('BattleSocket', `Event received: ${event}`, data);
          this.notifyListeners(event, data);
        });
      });

      this.socket.on('connect', () => {
        logger.info('BattleSocket', 'Connected to battle namespace');
        resolve();
      });

      this.socket.on('connect_error', (error) => {
        logger.error('BattleSocket', 'Connection error:', error.message);
        reject(error);
      });

      this.socket.on('disconnect', (reason) => {
        logger.info('BattleSocket', 'Disconnected from battle namespace:', reason);
      });

      // Таймаут подключения
      setTimeout(() => {
        if (!this.socket?.connected) {
          this.disconnect();
          reject(new Error('Battle connection timeout'));
        }
      }, 5000);
    });
  }

  disconnect() {
    if (this.socket) {
      logger.info('BattleSocket', 'Disconnecting from battle namespace');
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.listeners.clear();
  }

  emit(event: keyof BattleClientToServerEvents, data?: any) {
    if (!this.socket?.connected) {
      logger.error('BattleSocket', `Socket not connected for event: ${event}`);
      return;
    }
    logger.debug('BattleSocket', `Emitting event: ${event}`, data);
    this.socket.emit(event, data);
  }

  on(event: string, callback: Function) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);

    // Возвращаем функцию для отписки
    return () => {
      this.listeners.get(event)?.delete(callback);
    };
  }

  off(event: string, callback?: Function) {
    if (callback) {
      this.listeners.get(event)?.delete(callback);
    } else {
      this.listeners.delete(event);
    }
  }

  private notifyListeners(event: string, data: any) {
    const callbacks = this.listeners.get(event);
    if (callbacks) {
      callbacks.forEach(callback => callback(data));
    }
  }

  isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

// Singleton экземпляр
export const battleSocket = new BattleSocket();
